import { format } from 'date-fns'
import { vi } from 'date-fns/locale'
import { MoreHorizontal, Eye, Edit, Copy } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useGameTemplates } from '../hooks'
import { GameTypeEnum } from '../types'

const gameTypeLabels: Record<GameTypeEnum, string> = {
  [GameTypeEnum.QUIZ]: 'Quiz',
  [GameTypeEnum.LUCKYWHEEL]: 'Lucky Wheel',
  [GameTypeEnum.FLIPCARD]: 'Flip Card',
}

const gameTypeBadgeVariant: Record<
  GameTypeEnum,
  'default' | 'secondary' | 'outline'
> = {
  [GameTypeEnum.QUIZ]: 'default' as const,
  [GameTypeEnum.LUCKYWHEEL]: 'secondary' as const,
  [GameTypeEnum.FLIPCARD]: 'outline' as const,
}

export function GameTemplatesTable() {
  const { templates, loading, error } = useGameTemplates()

  if (loading) {
    return (
      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tên Template</TableHead>
              <TableHead>Loại Game</TableHead>
              <TableHead>Trạng thái</TableHead>
              <TableHead>Ngày tạo</TableHead>
              <TableHead>Cập nhật</TableHead>
              <TableHead className='w-[70px]'></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton className='h-5 w-48' />
                </TableCell>
                <TableCell>
                  <Skeleton className='h-5 w-24' />
                </TableCell>
                <TableCell>
                  <Skeleton className='h-5 w-24' />
                </TableCell>
                <TableCell>
                  <Skeleton className='h-4 w-24' />
                </TableCell>
                <TableCell>
                  <Skeleton className='h-4 w-24' />
                </TableCell>
                <TableCell>
                  <Skeleton className='h-8 w-8' />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='text-sm text-destructive'>Lỗi khi tải templates</div>
      </div>
    )
  }

  if (templates.length === 0) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='text-center'>
          <div className='text-sm text-muted-foreground'>
            Chưa có game template nào
          </div>
          <div className='mt-1 text-xs text-muted-foreground'>
            Tạo template đầu tiên để bắt đầu
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='rounded-md border'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tên Template</TableHead>
            <TableHead>Loại Game</TableHead>
            <TableHead>Trạng thái</TableHead>
            <TableHead>Ngày tạo</TableHead>
            <TableHead>Cập nhật</TableHead>
            <TableHead className='w-[70px]'></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template.id}>
              <TableCell className='font-medium'>
                {template.templateName}
              </TableCell>
              <TableCell>
                <Badge variant={gameTypeBadgeVariant[template.gameType]}>
                  {gameTypeLabels[template.gameType]}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant={template.isActive ? 'default' : 'secondary'}>
                  {template.isActive ? 'Hoạt động' : 'Tạm dừng'}
                </Badge>
              </TableCell>
              <TableCell>
                {format(new Date(template.createdAt), 'dd/MM/yyyy', {
                  locale: vi,
                })}
              </TableCell>
              <TableCell>
                {format(new Date(template.updatedAt), 'dd/MM/yyyy', {
                  locale: vi,
                })}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='ghost' className='h-8 w-8 p-0'>
                      <span className='sr-only'>Mở menu</span>
                      <MoreHorizontal className='h-4 w-4' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem>
                      <Eye className='mr-2 h-4 w-4' />
                      Xem chi tiết
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Edit className='mr-2 h-4 w-4' />
                      Chỉnh sửa
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Copy className='mr-2 h-4 w-4' />
                      Sao chép
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
